import { useContext, useEffect, useMemo } from "react";
import { useStyles } from "./SecondaryCodeRequirements.styles";
import { MainTable } from "components";
import { useSecondaryCodeRequirementsColumns } from "./hooks/useSecondaryCodeRequirementsColumns";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { useParams } from "react-router-dom";
import { getDowntimeDurationInHoursMinutes } from "utilities/utilities";
import { IEventReportRow } from "./SecondaryCodeRequerimentsInterfaces";
import { useTranslation } from "react-i18next";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { SecondaryCodesTableColumnsIdEnum } from "./hooks/useSecondaryCodeRequirementsColumnsInterface";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { Order } from "components/MainTable/components/EnhancedTableHead/EnhancedTableHeadInterfaces";

export const SecondaryCodeRequirements = () => {
    const classes = useStyles();
    const { millId } = useParams();
    const { i18n } = useTranslation();
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();

    const queryTableOperationFilters = useMemo(
        () => ({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            ...(orderBy === SecondaryCodesTableColumnsIdEnum.AREA && order === "asc"
                ? { order: "asc_nulls_first" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.AREA && order === "desc"
                ? { order: "desc_nulls_last" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.FUNCTIONAL_LOCATION && order === "asc"
                ? { order: "asc_nulls_first" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.FUNCTIONAL_LOCATION && order === "desc"
                ? { order: "desc_nulls_last" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.MACHINE_CENTER && order === "asc"
                ? { order: "asc_nulls_first" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.MACHINE_CENTER && order === "desc"
                ? { order: "desc_nulls_last" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.SECONDARY_FAILURE_CODE && order === "asc"
                ? { order: "asc_nulls_first" as Order }
                : orderBy === SecondaryCodesTableColumnsIdEnum.SECONDARY_FAILURE_CODE && order === "desc"
                ? { order: "desc_nulls_last" as Order }
                : { order }),
            orderBy,
        }),
        [page, rowsPerPage, order, orderBy],
    );
    const millIdMemoized = useMemo(() => (Boolean(millId) ? Number(millId) : 0), [millId]);
    const { showOnlyPending, setPendingEventReportsCount } = useContext(MillDashboardContext);
    const { loading, eventReports, apolloQueryError, refetch, totalCount } =
        EventReportsEntity.GetEventReportsWithSecondaryCodesBO(
            showOnlyPending,
            millIdMemoized,
            queryTableOperationFilters,
        );

    useEffect(() => {
        if (showOnlyPending && !loading && !apolloQueryError) {
            setPendingEventReportsCount(totalCount);
        }
    }, [showOnlyPending, loading, apolloQueryError, totalCount, setPendingEventReportsCount]);

    const parsedEventReports = useMemo(
        () =>
            eventReports.map((eventReport) => {
                const parsedEventReport = {
                    ...(eventReport as unknown as IEventReportRow),
                    [SecondaryCodesTableColumnsIdEnum.DURATION]: eventReport.end_date
                        ? getDowntimeDurationInHoursMinutes(eventReport.valid_time, true)
                        : null,
                    [SecondaryCodesTableColumnsIdEnum.MACHINE_CENTER]:
                        eventReport.machine_center?.short_name || eventReport.machine_center?.name,
                    [SecondaryCodesTableColumnsIdEnum.AREA]: eventReport.failure_type.is_location_specific
                        ? eventReport?.area
                            ? handleNameTranslation(i18n.language, eventReport.area.translation.translation_names)
                            : ""
                        : "N/A",
                    [SecondaryCodesTableColumnsIdEnum.FUNCTIONAL_LOCATION]: eventReport.failure_type
                        .is_location_specific
                        ? eventReport?.functional_location
                            ? handleNameTranslation(
                                  i18n.language,
                                  eventReport.functional_location.translation.translation_names,
                              )
                            : ""
                        : "N/A",
                    [SecondaryCodesTableColumnsIdEnum.FAILURE_TYPE]: translateDefaultEntity(
                        i18n.language,
                        eventReport.failure_type,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                    ),
                    [SecondaryCodesTableColumnsIdEnum.FAILURE_CODE]:
                        translateDefaultEntity(
                            i18n.language,
                            eventReport.failure_code,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                        ) || "N/A",
                };

                return parsedEventReport;
            }),
        [eventReports, i18n.language],
    ) as unknown as IEventReportRow[];

    const tableColumns = useSecondaryCodeRequirementsColumns(refetch) as unknown as IMainTableColumn[];

    return (
        <div className={classes.mainWrapper}>
            <MainTable
                columns={tableColumns}
                rows={!loading && !apolloQueryError ? parsedEventReports : []}
                loading={loading}
                pagination
                count={totalCount}
                tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                handleRequestSortParent={handleRequestSort}
                handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                handleChangePageParent={handleChangePage}
                querySorted={orderBy !== SecondaryCodesTableColumnsIdEnum.DURATION}
            />
        </div>
    );
};
